import { Banner, Empty } from "../ui";
import { BarChartCard, type BarDatum } from "../charts";
import { Card, MetricCard, dash, fmtInt, fmtMs, fmtPct, num, rateColor } from "./format";
import type { SuiteDetail } from "./types";

// ============================================================ cache tab

/**
 * THE HONEST STATE COMES FIRST. `eval_turns` may carry no plan-cache column, and
 * a hit rate is exactly the kind of number that is flattering as a zero and
 * catastrophic as a fabricated one. Until the column exists this tab names the
 * field it is waiting for; the moment the harness writes it, `instrumented`
 * flips and the real rates render with no further change here.
 */
export function CacheTab({ detail }: { detail: SuiteDetail | null }) {
  const cache = detail?.cache ?? {};
  const repeats = detail?.repeated_replies ?? [];
  const total = num(detail?.total);
  const repeatedTurns = repeats.reduce((sum, r) => sum + num(r.n), 0);
  const laneBars: BarDatum[] = (cache.by_lane ?? []).map((l) => ({
    label: l.lane || "(blank)",
    value: num(l.hit_rate) * 100,
  }));

  return (
    <div className="stack">
      <Card title="Plan cache">
        {!cache.instrumented ? (
          <Banner tone="warning">
            Not instrumented. {cache.note ?? "no turn in this run recorded a cache flag"}. This tab reads
            <code> plan_cache_hit</code> / <code>cache_hit</code> on <code>eval_turns</code> and renders the real hit
            rate as soon as one of them is written. A 0% would be a claim this run cannot support, so none is shown.
          </Banner>
        ) : (
          <>
            <div className="grid c3">
              <MetricCard
                label="Hit rate"
                value={fmtPct(cache.hit_rate)}
                sub={`${fmtInt(cache.hits)} / ${fmtInt(cache.observed_turns)} instrumented turns`}
                sub2={
                  num(cache.observed_turns) < num(cache.n)
                    ? `${fmtInt(num(cache.n) - num(cache.observed_turns))} of ${fmtInt(
                        cache.n
                      )} turns recorded no flag and are excluded`
                    : undefined
                }
                hint="The denominator is the turns that RECORDED a flag, not every turn: dividing by turns the harness never instrumented reports a miss rate the run never measured."
              />
            </div>
            {laneBars.length > 0 && (
              <div style={{ marginTop: "var(--sp-3)" }}>
                <BarChartCard title="Hit rate by lane" data={laneBars} horizontal yLabel="%" note="Percent." />
              </div>
            )}
            <div className="table-wrap" style={{ marginTop: "var(--sp-3)" }}>
              <table className="data">
                <thead>
                  <tr>
                    <th>Lane</th>
                    <th className="num">Turns</th>
                    <th className="num">Hits</th>
                    <th className="num">Hit rate</th>
                    <th className="num">Avg latency</th>
                  </tr>
                </thead>
                <tbody>
                  {(cache.by_lane ?? []).map((row) => (
                    <tr key={row.lane || "(blank)"}>
                      <td>{row.lane || "(blank)"}</td>
                      <td className="num">{fmtInt(row.n)}</td>
                      <td className="num">{fmtInt(row.hits)}</td>
                      <td className="num" style={{ color: rateColor(row.hit_rate, num(row.n)) }}>
                        {fmtPct(row.hit_rate, 0)}
                      </td>
                      <td className="num">{fmtMs(row.avg_latency_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      <Card
        title="Repeated replies"
        note={`This is NOT a cache hit rate. It is the only repetition signal this run recorded: replies emitted verbatim on more than one turn. ${fmtInt(
          repeatedTurns
        )} of ${fmtInt(total)} turns sent a reply that also appeared elsewhere in the run.`}
      >
        {!repeats.length ? (
          <Empty title="Every reply is unique" body="No repetition to report in this run." />
        ) : (
          <div className="col">
            {repeats.map((row, i) => (
              <div key={i} className="note">
                <div className="who">
                  <span className="name">×{fmtInt(row.n)}</span>
                  <span>
                    {fmtInt(row.conversations)} conversation{num(row.conversations) === 1 ? "" : "s"}
                  </span>
                  <span style={{ color: rateColor(row.pass_rate, num(row.n)) }}>
                    {fmtPct(row.pass_rate, 0)} pass
                  </span>
                  <span>{fmtMs(row.avg_latency_ms)} avg</span>
                </div>
                <div className="body">{dash(row.reply_text)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
